import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Gear, SignOut, User, CurrencyCircleDollar } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { authPut } from '../utils/api';

const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
];

export default function Settings() {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [form, setForm] = useState({
    name: user?.name || '',
    currency: user?.currency || 'USD'
  });

  const handleSave = async () => {
    setSaving(true);
    setSuccess(false);

    try {
      await authPut('/api/settings', form);
      updateUser(form);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (e) {
      console.error('Save settings error:', e);
    }
    setSaving(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Settings</h1>
        <p className="text-[#9CA3AF] mt-1">Manage your account preferences</p>
      </div>

      {/* Profile Section */}
      <div className="card-default p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[#3B82F6]/20 flex items-center justify-center">
            <User size={20} className="text-[#3B82F6]" />
          </div>
          <h2 className="font-semibold text-lg">Profile</h2>
        </div>

        <div className="space-y-5">
          <div className="space-y-2">
            <Label className="text-[#F9FAFB]">Email</Label>
            <Input
              value={user?.email || ''}
              disabled
              className="input-default w-full opacity-50"
            />
            <p className="text-xs text-[#9CA3AF]">Email cannot be changed</p>
          </div>

          <div className="space-y-2">
            <Label className="text-[#F9FAFB]">Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="input-default w-full"
              data-testid="settings-name"
            />
          </div>
        </div>
      </div>

      {/* Currency Section */}
      <div className="card-default p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[#10B981]/20 flex items-center justify-center">
            <CurrencyCircleDollar size={20} className="text-[#10B981]" />
          </div>
          <h2 className="font-semibold text-lg">Currency</h2>
        </div>

        <div className="space-y-2">
          <Label className="text-[#F9FAFB]">Preferred Currency</Label>
          <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
            <SelectTrigger className="input-default" data-testid="settings-currency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#111827] border-white/10">
              {CURRENCIES.map(c => (
                <SelectItem key={c.code} value={c.code} className="text-[#F9FAFB] focus:bg-[#1F2937]">
                  {c.symbol} {c.name} ({c.code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex items-center gap-4">
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="btn-primary"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            'Save Changes'
          )}
        </Button>
        {success && (
          <span className="text-[#10B981] text-sm">Settings saved!</span>
        )}
      </div>

      {/* Account Section */}
      <div className="card-default p-6 border-[#EF4444]/20">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[#EF4444]/20 flex items-center justify-center">
            <Gear size={20} className="text-[#EF4444]" />
          </div>
          <h2 className="font-semibold text-lg">Account</h2>
        </div>

        <Button 
          onClick={handleLogout}
          className="bg-[#EF4444]/10 text-[#EF4444] hover:bg-[#EF4444]/20 border border-[#EF4444]/20"
          data-testid="logout-btn"
        >
          <SignOut size={18} className="mr-2" />
          Sign Out
        </Button>
      </div>

      {/* Info */}
      <div className="card-secondary p-4 text-center">
        <p className="text-xs text-[#9CA3AF]">
          UniWallet v1.0 • Built with AI for university students
        </p>
      </div>
    </div>
  );
}
